import * as Sentry from "@sentry/react";
import { useT } from "@transifex/react";
import moment, { type Moment } from "moment";
import type { AeaExerciseModel } from "p6m-p6u";
import { useEffect, useRef, useState } from "react";
import { useGeneralContext } from "../context/AppGeneralContext";
import { useBookContext } from "../context/BookContext";
import { useRTContext } from "../context/RTContext";
import { useTestCreationContext } from "../context/TestCreationContext";
import { NO_CLASS_SELECTION, ShareSteps, ShareTimings } from "../enums/shareFlow";
import { SchoolClassVisibilities } from "../enums/schoolClasses";
import {
    ActivityTypeProps,
    SHARE_STEP_PROPS,
    toClassVisibilityProp,
    toIsFirstClassProps,
} from "../helpers/analyticsEventProps";
import { ToastVariants } from "../enums/toast";
import {
    getVocabTrainingMetadata,
    recreateVocabTrainingMetadataWithoutSchoolClass,
    shareVocabTrainingMetadata,
    type VocabTrainingMetadataPayload,
} from "../networking/tests";
import {
    collapseClassForm,
    executeShareWithClassRollback,
    hasEffectiveClass,
    resolveEmptyClassInviteModal,
    ShareWithClassRollbackStages,
} from "../helpers/ShareFlow";
import { ActivityVisibility, clampPlannedDate, resolveVocabActivityVisibility } from "../helpers/ActivityVisibility";
import { isOwnContentTraining, resolveArticleUuid, resolveMetadataJson } from "../helpers/UserExercises";
import type { NewClassFormState } from "../complex/classActivityCard/ClassActivityCardsOverview.types";
import { getUniqueDefaultClassName } from "../utils/schoolClassUtils";
import { useShareFlowState } from "./useShareFlowState";
import { useLogger } from "./useLogger";

const SHARE_TOAST_DURATION_SUCCESS_MS = 3000;
const SHARE_TOAST_DURATION_ERROR_MS = 5000;

export interface VocabSharePopupState {
    trainingId: string | null;
    training: AeaExerciseModel | null;
    bookName: string;
    vocabularyCount: number;
    step: ShareSteps.CLASS | ShareSteps.TIMING;
    selectedSchoolClassId: string | null;
    timing: ShareTimings;
    visibilityDate: Moment | null;
    newClassForm: NewClassFormState | null;
}

const INITIAL_VOCAB_SHARE_POPUP_STATE: VocabSharePopupState = {
    trainingId: null,
    training: null,
    bookName: "",
    vocabularyCount: 0,
    step: ShareSteps.CLASS,
    selectedSchoolClassId: null,
    timing: ShareTimings.NOW,
    visibilityDate: null,
    newClassForm: null,
};

interface VocabShareToastState {
    isOpen: boolean;
    message: string;
    variant: ToastVariants;
    // Bumped on each show so consecutive toasts re-trigger the Radix root even with the same message.
    toastKey: number;
}

interface BuildMetadataPayloadParams {
    trainingId: string;
    training: AeaExerciseModel;
    teacherId: string;
    bookName: string;
    vocabularyCount: number;
    schoolClassId: string | null;
    visibilityDate: Moment | null;
    json: string | undefined;
    shareLink: string;
}

const _buildMetadataPayload = (params: BuildMetadataPayloadParams): VocabTrainingMetadataPayload => ({
    testUuid: params.trainingId,
    teacherId: params.teacherId,
    articleUuid: resolveArticleUuid(params.training, params.teacherId),
    json: params.json,
    schoolClassId: params.schoolClassId,
    testName: params.training.testName ?? "",
    bookName: params.bookName,
    vocabularyCount: params.vocabularyCount,
    shareLink: params.shareLink,
    visibilityDate: params.visibilityDate ? params.visibilityDate.valueOf() : null,
});

export interface OpenShareFlowParams {
    training: AeaExerciseModel;
    bookName: string;
    vocabularyCount: number;
}

export interface DuplicateVocabTrainingPopupState {
    training: AeaExerciseModel;
    // Display name as shown on the card: legacy trainings without a title fall back to the book/subject name.
    name: string;
    bookName: string;
}

export const useVocabTrainingOperations = (schoolClassId?: string) => {
    const t = useT();
    const {
        deleteVocabTraining,
        duplicateVocabTraining,
        schoolClasses,
        createSchoolClass,
        deleteSchoolClass,
        updateVocabTrainingSchoolClass,
        vocabTrainings,
        vocabTrainingsDataLoaded,
        vocabTrainingsLoadError,
        fetchAndStoreVocabTrainings,
        vocabMetadata,
        setVocabMetadata,
        hasLoadedSchoolClasses,
        schoolClassesLoadError,
    } = useRTContext();
    const { userId } = useGeneralContext();
    const { loadBooksInfoFromUUIDsIntoState } = useBookContext();
    const { loadUserOwnSubjectUUIDsInfoIntoState, userUsedOwnSubjectsInfo } = useTestCreationContext();
    const { logEvent } = useLogger();

    const t_sharedWithClass = t("Shared with class", { _tags: "ShareFlow, ShareToast" });
    const t_defaultClassName = t("My newest class", { _tags: "ShareFlow, useVocabTrainingOperations" });
    const t_errCreate = t("There was an error while creating the new class. The vocabulary training was not shared.", {
        _tags: "ShareFlow, useVocabTrainingOperations",
    });
    const t_errUpdateWithRollback = t(
        "There was an error while assigning the class to the vocabulary training. The newly created class has been removed and the training was not shared.",
        { _tags: "ShareFlow, useVocabTrainingOperations" }
    );
    const t_errUpdateNoRollback = t(
        "There was an error while assigning the class to the vocabulary training. The training was not shared.",
        { _tags: "ShareFlow, useVocabTrainingOperations" }
    );
    const t_errShareWithRollback = t(
        "There was an error while sharing the vocabulary training. The newly created class has been removed.",
        { _tags: "ShareFlow, useVocabTrainingOperations" }
    );
    const t_errShareNoRollback = t("There was an error while sharing the vocabulary training. Please try again.", {
        _tags: "ShareFlow, useVocabTrainingOperations",
    });
    const t_visibilityUpdated = t("Visibility updated", { _tags: "ClassActivity, EditActivityVisibilityModal" });
    const t_errEditVisibility = t("There was an error while updating the visibility. Please try again.", {
        _tags: "ClassActivity, EditActivityVisibilityModal",
    });

    const [vocabTrainingDataToDelete, setVocabTrainingDataToDelete] = useState<AeaExerciseModel | null>(null);
    const [duplicatePopup, setDuplicatePopup] = useState<DuplicateVocabTrainingPopupState | null>(null);
    const [keepClassOnDuplicate, setKeepClassOnDuplicate] = useState(false);
    const [shareLinkPopupTraining, setShareLinkPopupTraining] = useState<AeaExerciseModel | null>(null);

    const [sharePopup, setSharePopup] = useState<VocabSharePopupState>(INITIAL_VOCAB_SHARE_POPUP_STATE);
    const [editActivityVisibilityPopup, setEditActivityVisibilityPopup] = useState<{ trainingId: string } | null>(null);
    const [shareToast, setShareToast] = useState<VocabShareToastState>({
        isOpen: false,
        message: "",
        variant: ToastVariants.SUCCESS,
        toastKey: 0,
    });
    const shareToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Two close calls in the same tick both see the stale open state — the ref dedupes the
    // abandon event where the state guard alone cannot.
    const hasLoggedShareAbandonRef = useRef(false);

    const {
        shareInProgress,
        setShareInProgress,
        inviteModal,
        setInviteModal,
        handleCloseInviteModal,
        handleInviteNavigate,
        handleSelectShareClass,
        handleCloseNewClassForm,
        handleCollapseNewClassForm,
        handleNewClassNameChange,
        handleToggleNewClassConsent,
        handleSelectShareTiming,
        handleShareVisibilityDateChange,
    } = useShareFlowState(setSharePopup);

    useEffect(() => {
        return () => {
            if (shareToastTimeoutRef.current) clearTimeout(shareToastTimeoutRef.current);
        };
    }, []);

    // Own-content rows key recent-articles off json; resolveMetadataJson documents the precedence.
    const _resolveMetadataJson = (training: AeaExerciseModel, storedJson?: string): Promise<string | undefined> =>
        resolveMetadataJson({
            training,
            userId,
            ownSubjectsInfo: userUsedOwnSubjectsInfo,
            storedJson: storedJson ?? (training.id ? vocabMetadata[training.id]?.json : undefined),
            loadOwnSubjectsInfo: loadUserOwnSubjectUUIDsInfoIntoState,
        });

    /** Loads the book / own-subject infos (covers, names) the training cards render. */
    const resolveVocabTrainingInfos = (trainings: AeaExerciseModel[]) => {
        // Book and subject info are resolved one request per training, so inside a class we restrict
        // them to that class's trainings — the others are never rendered there.
        const trainingsToResolve = schoolClassId
            ? trainings.filter((training) => training.groupId === schoolClassId)
            : trainings;

        // Load book info for trainings that have book IDs
        const bookIdsToLoad = trainingsToResolve
            .filter((training) => !isOwnContentTraining(training, userId) && training.subjectContentId?.id)
            .map((training) => training.subjectContentId!.id!);

        if (bookIdsToLoad.length > 0) {
            loadBooksInfoFromUUIDsIntoState(bookIdsToLoad);
        }

        // Load own subject info for trainings with own content
        const ownSubjectIdsToLoad = trainingsToResolve
            .filter((training) => isOwnContentTraining(training, userId) && training.subjectContentId?.id)
            .map((training) => training.subjectContentId!.id!);

        if (ownSubjectIdsToLoad.length > 0) {
            loadUserOwnSubjectUUIDsInfoIntoState(ownSubjectIdsToLoad);
        }
    };

    const loadVocabTrainings = async (): Promise<AeaExerciseModel[] | null> => {
        const rawVocabTrainings = await fetchAndStoreVocabTrainings();
        if (!rawVocabTrainings) return null;
        resolveVocabTrainingInfos(rawVocabTrainings);
        return rawVocabTrainings;
    };

    /**
     * Students in public classes aren't logged in and read only the metadata row, so a published row
     * without a link shows them a training they cannot start. The row is fetched live on a cache miss:
     * the bulk GET skips class-less trainings, and create writes the link into the row before the
     * trainings list carries it. The final list refetch covers pxp lagging behind on a fresh training.
     * Empty means the caller must abort.
     */
    const _resolveShareLinkForPublish = async (
        trainingId: string,
        training: AeaExerciseModel,
        cachedRow: VocabTrainingMetadataPayload | undefined = vocabMetadata[trainingId]
    ): Promise<string> => {
        const row = cachedRow ?? (await getVocabTrainingMetadata(trainingId).catch(() => undefined));
        if (row?.shareLink) return row.shareLink;
        const fromList = vocabTrainings.find((vt) => vt.id === trainingId)?.shareLink || training.shareLink;
        if (fromList) return fromList;
        const refreshed = await loadVocabTrainings();
        return refreshed?.find((vt) => vt.id === trainingId)?.shareLink ?? "";
    };

    const _reportMissingShareLink = (trainingId: string) => {
        Sentry.captureException(new Error("Vocab training has no share link, publish aborted"), {
            extra: { vocabTrainingId: trainingId },
        });
    };

    const handleOpenDeleteVocabTrainingModal = (training: AeaExerciseModel) => {
        setVocabTrainingDataToDelete(training);
    };

    const handleConfirmDeleteVocabTraining = async () => {
        if (vocabTrainingDataToDelete?.id) {
            const deletedId = vocabTrainingDataToDelete.id;
            await deleteVocabTraining(deletedId);
            setVocabTrainingDataToDelete(null);
            await loadVocabTrainings();
        }
    };

    const handleOpenDuplicateVocabTrainingPopup = (
        params: DuplicateVocabTrainingPopupState,
        keepClassByDefault = false
    ) => {
        if (!params.training.id) return;
        setKeepClassOnDuplicate(keepClassByDefault);
        setDuplicatePopup(params);
    };

    const handleCloseDuplicateVocabTrainingPopup = () => setDuplicatePopup(null);

    const toggleKeepClassOnDuplicate = () => setKeepClassOnDuplicate((prev) => !prev);

    const handleConfirmDuplicateVocabTraining = async (newName: string) => {
        if (!duplicatePopup) return;
        const targetSchoolClassId = keepClassOnDuplicate ? duplicatePopup.training.groupId : undefined;
        const isDuplicated = await duplicateVocabTraining(
            duplicatePopup.training,
            newName,
            duplicatePopup.bookName,
            targetSchoolClassId
        );
        if (!isDuplicated) return;
        setDuplicatePopup(null);
        await loadVocabTrainings();
    };

    const handleOpenShareFlow = (params: OpenShareFlowParams) => {
        if (!params.training.id) return;
        logEvent("started share flow", {
            activity_type: ActivityTypeProps.VOCAB_TRAINING,
            training_id: params.training.id,
        });
        hasLoggedShareAbandonRef.current = false;
        setSharePopup({
            ...INITIAL_VOCAB_SHARE_POPUP_STATE,
            trainingId: params.training.id,
            training: params.training,
            bookName: params.bookName,
            vocabularyCount: params.vocabularyCount,
            selectedSchoolClassId: params.training.groupId ?? NO_CLASS_SELECTION,
        });
    };

    const _resetSharePopupState = () => {
        setSharePopup(INITIAL_VOCAB_SHARE_POPUP_STATE);
    };

    // Public close for the popup's onClose prop (X / overlay / Abbrechen). Internal completion paths
    // call _resetSharePopupState directly because they run while shareInProgress is still true.
    const handleCloseSharePopup = () => {
        if (shareInProgress) return;
        // Guard against a close on an already-closed popup reporting a phantom abandon.
        if (sharePopup.trainingId && !hasLoggedShareAbandonRef.current) {
            hasLoggedShareAbandonRef.current = true;
            logEvent("abandoned share flow", {
                activity_type: ActivityTypeProps.VOCAB_TRAINING,
                last_step: SHARE_STEP_PROPS[sharePopup.step],
                has_class_selected: hasEffectiveClass(sharePopup.selectedSchoolClassId, sharePopup.newClassForm),
            });
        }
        _resetSharePopupState();
    };

    const handleCloseShareLinkPopup = () => {
        setShareLinkPopupTraining(null);
    };

    const handleVocabTrainingLinkCopied = (training: AeaExerciseModel | null) => {
        if (!training) return;
        logEvent("Copied Exercise Link", {
            // exerciseId/groupId are grandfathered — kept so the existing series stay continuous.
            // training_id/class_id are the canonical names the class and share events use.
            exerciseId: training.id,
            training_id: training.id,
            groupId: training.groupId,
            ...(training.groupId ? { class_id: training.groupId } : {}),
        });
    };

    const _showShareToast = (message: string, variant: ToastVariants, durationOverrideMs?: number) => {
        if (shareToastTimeoutRef.current) clearTimeout(shareToastTimeoutRef.current);
        setShareToast((prev) => ({ isOpen: true, message, variant, toastKey: prev.toastKey + 1 }));
        const durationMs =
            durationOverrideMs ??
            (variant === ToastVariants.ERROR ? SHARE_TOAST_DURATION_ERROR_MS : SHARE_TOAST_DURATION_SUCCESS_MS);
        shareToastTimeoutRef.current = setTimeout(
            () => setShareToast((prev) => ({ ...prev, isOpen: false })),
            durationMs
        );
    };

    /**
     * A metadata row exists for unshared trainings too (registered on create), so metadata presence
     * can't tell shared from unshared.
     */
    const handleShareButtonClick = async (params: OpenShareFlowParams) => {
        const trainingId = params.training.id;
        if (!trainingId) return;

        if (!params.training.groupId) {
            handleOpenShareFlow(params);
            return;
        }

        let metadata = vocabMetadata[trainingId];
        if (!metadata) {
            // Cache miss (list metadata still loading, or its batch GET failed): resolve live so an
            // already-shared training can't be misread as HIDDEN and pushed through a re-share.
            try {
                const fetched = await getVocabTrainingMetadata(trainingId);
                if (fetched) {
                    metadata = fetched;
                    setVocabMetadata((prev) => ({ ...prev, [trainingId]: fetched }));
                }
            } catch {
                _showShareToast(t_errShareNoRollback, ToastVariants.ERROR);
                return;
            }
        }

        const { visibility } = resolveVocabActivityVisibility(metadata?.visibilityDate);
        if (visibility === ActivityVisibility.HIDDEN) {
            handleOpenShareFlow(params);
        } else {
            setShareLinkPopupTraining(params.training);
        }
    };

    const _abortShareWithoutLink = (trainingId: string) => {
        _reportMissingShareLink(trainingId);
        _showShareToast(t_errShareNoRollback, ToastVariants.ERROR);
        _resetSharePopupState();
        setShareInProgress(false);
    };

    // If the metadata POST fails after we already cleared the class, restore the previous class so the
    // training isn't silently un-assigned.
    const handleNoClassWeiter = async () => {
        const trainingId = sharePopup.trainingId;
        const training = sharePopup.training;
        const bookName = sharePopup.bookName;
        const vocabularyCount = sharePopup.vocabularyCount;
        if (!trainingId || !training) return;

        const currentTraining = vocabTrainings.find((vt) => vt.id === trainingId);
        const previousSchoolClassId = currentTraining?.groupId ?? null;
        let classCleared = false;
        setShareInProgress(true);
        const shareLink = await _resolveShareLinkForPublish(trainingId, training);
        if (!shareLink) {
            _abortShareWithoutLink(trainingId);
            return;
        }
        try {
            if (previousSchoolClassId !== null) {
                await updateVocabTrainingSchoolClass(trainingId, null, {
                    silentOnError: true,
                    skipMetadataSync: true,
                });
                classCleared = true;
            }

            const metadataPayload = _buildMetadataPayload({
                trainingId,
                training,
                teacherId: userId,
                bookName,
                vocabularyCount,
                schoolClassId: null,
                visibilityDate: moment(),
                json: await _resolveMetadataJson(training),
                shareLink,
            });
            // Recreate rather than plain-share: the delete leg also heals rows left with a stale
            // schoolClass before this route existed.
            await recreateVocabTrainingMetadataWithoutSchoolClass(metadataPayload);
            // Mirror the just-POSTed payload into the local map so the card's visibility decoder
            // sees fresh state immediately, without waiting on the bulk-GET refresh.
            setVocabMetadata((prev) => ({ ...prev, [trainingId]: metadataPayload }));
            logEvent("VTG - Vocab Training Shared", {
                // trainingId is grandfathered — kept so the existing series stays continuous.
                // training_id is the canonical name `started share flow` uses.
                trainingId,
                training_id: trainingId,
                hasClass: false,
                isScheduled: false,
            });
            const refreshed = await loadVocabTrainings();
            _resetSharePopupState();
            // Fallback snapshot clears groupId so the QR popup can't show the code of the just-removed class.
            const refreshedTraining = refreshed?.find((vt) => vt.id === trainingId) ?? {
                ...training,
                groupId: undefined,
            };
            // The list can still lag behind the row, so the popup shows the link that was just published.
            setShareLinkPopupTraining({ ...refreshedTraining, shareLink });
        } catch {
            if (classCleared) {
                try {
                    await updateVocabTrainingSchoolClass(trainingId, previousSchoolClassId, {
                        silentOnError: true,
                        skipMetadataSync: true,
                    });
                } catch {
                    console.warn("Failed to restore previous class on no-class share rollback", {
                        trainingId,
                        previousSchoolClassId,
                    });
                }
            }
            _showShareToast(t_errShareNoRollback, ToastVariants.ERROR);
            _resetSharePopupState();
        } finally {
            setShareInProgress(false);
        }
    };

    const handleShareNextStep = () => {
        if (sharePopup.step === ShareSteps.CLASS && sharePopup.selectedSchoolClassId === NO_CLASS_SELECTION) {
            void handleNoClassWeiter();
            return;
        }
        setSharePopup((prev) => {
            if (prev.step !== ShareSteps.CLASS) return prev;
            const committingNewClass = !!prev.newClassForm?.isOpen;
            const nextNewClassForm = prev.newClassForm
                ? collapseClassForm(prev.newClassForm, committingNewClass)
                : prev.newClassForm;
            return {
                ...prev,
                step: ShareSteps.TIMING,
                newClassForm: nextNewClassForm,
                selectedSchoolClassId: committingNewClass ? null : prev.selectedSchoolClassId,
            };
        });
    };

    const handleSharePrevStep = () => {
        setSharePopup((prev) => {
            if (prev.step !== ShareSteps.TIMING) return prev;
            const nextNewClassForm = prev.newClassForm
                ? collapseClassForm(prev.newClassForm, false)
                : prev.newClassForm;
            return { ...prev, step: ShareSteps.CLASS, newClassForm: nextNewClassForm };
        });
    };

    // Final action = TIMING step (commits with class) OR CLASS step + "Keine Klasse" (short-circuit share).
    const isFinalShareAction =
        sharePopup.step === ShareSteps.TIMING || sharePopup.selectedSchoolClassId === NO_CLASS_SELECTION;

    const handleSharePrimaryAction = () => {
        if (sharePopup.step === ShareSteps.TIMING) {
            void handleConfirmShare();
        } else {
            handleShareNextStep();
        }
    };

    const handleOpenNewClassForm = () => {
        // Only the first open is an intent to create — re-opening a collapsed draft is navigation.
        if (!sharePopup.newClassForm) {
            logEvent("started class creation", { source: "share_flow_vocab_training" });
        }
        setSharePopup((prev) => {
            if (prev.newClassForm) {
                return { ...prev, selectedSchoolClassId: null, newClassForm: { ...prev.newClassForm, isOpen: true } };
            }
            const existingNames = schoolClasses.map((c) => c.name);
            const defaultName = getUniqueDefaultClassName(t_defaultClassName, existingNames);
            return {
                ...prev,
                selectedSchoolClassId: null,
                newClassForm: {
                    name: defaultName,
                    hasConsent: false,
                    isOpen: true,
                    isCommitted: false,
                    committedName: null,
                },
            };
        });
    };

    const handleConfirmShare = async () => {
        const trainingId = sharePopup.trainingId;
        const training = sharePopup.training;
        if (!trainingId || !training) return;

        const bookName = sharePopup.bookName;
        const vocabularyCount = sharePopup.vocabularyCount;
        const newClassForm = sharePopup.newClassForm;
        // Read groupId from the live state — sharePopup.training is a snapshot from when the popup opened
        // and may be stale if the training was reassigned in the meantime.
        const currentTraining = vocabTrainings.find((vt) => vt.id === trainingId);
        const previousSchoolClassId = currentTraining?.groupId ?? null;
        const submitNow = moment();
        // A planned date that slipped into the past while the teacher dwelled in the popup is clamped
        // back to submitNow, which shares right away — so it no longer counts as scheduled either.
        const visibilityDate =
            sharePopup.timing === ShareTimings.LATER && sharePopup.visibilityDate
                ? clampPlannedDate(sharePopup.visibilityDate, submitNow)
                : submitNow;
        const isScheduled = visibilityDate.isAfter(submitNow);

        setShareInProgress(true);
        const shareLink = await _resolveShareLinkForPublish(trainingId, training);
        if (!shareLink) {
            _abortShareWithoutLink(trainingId);
            return;
        }

        const result = await executeShareWithClassRollback({
            activityId: trainingId,
            selectedSchoolClassId: sharePopup.selectedSchoolClassId,
            previousSchoolClassId,
            newClassForm,
            createSchoolClass,
            deleteSchoolClass,
            updateActivitySchoolClass: (activityId, targetSchoolClassId, options) =>
                updateVocabTrainingSchoolClass(activityId, targetSchoolClassId, { ...options, skipMetadataSync: true }),
            shareActivity: async (effectiveSchoolClassId) => {
                const metadataPayload = _buildMetadataPayload({
                    trainingId,
                    training,
                    teacherId: userId,
                    bookName,
                    vocabularyCount,
                    schoolClassId: effectiveSchoolClassId,
                    visibilityDate,
                    json: await _resolveMetadataJson(training),
                    shareLink,
                });
                await shareVocabTrainingMetadata(metadataPayload);
                // Mirror the just-POSTed payload into the local map so the card's visibility
                // decoder sees fresh state immediately, without waiting on the bulk-GET refresh.
                setVocabMetadata((prev) => ({ ...prev, [trainingId]: metadataPayload }));
            },
            getErrorMessage: ({ stage, rolledBack }) => {
                switch (stage) {
                    case ShareWithClassRollbackStages.CREATE:
                        return t_errCreate;
                    case ShareWithClassRollbackStages.UPDATE:
                        return rolledBack ? t_errUpdateWithRollback : t_errUpdateNoRollback;
                    case ShareWithClassRollbackStages.SHARE:
                        return rolledBack ? t_errShareWithRollback : t_errShareNoRollback;
                }
            },
        });

        try {
            if (!result.ok) {
                _showShareToast(result.errorMessage, ToastVariants.ERROR);
                _resetSharePopupState();
                return;
            }

            const { effectiveSchoolClassId, createdSchoolClassId } = result;

            // Deferred from createSchoolClass (suppressCreatedEvent): a rolled-back class must not
            // be counted, so the event fires only once the whole share has succeeded. The render-
            // scoped `schoolClasses` is still the pre-create list here.
            if (createdSchoolClassId) {
                logEvent("Created Class", {
                    groupId: createdSchoolClassId,
                    class_id: createdSchoolClassId,
                    class_visibility: toClassVisibilityProp(SchoolClassVisibilities.PRIVATE),
                    ...toIsFirstClassProps(hasLoadedSchoolClasses && !schoolClassesLoadError, schoolClasses.length),
                });
            }

            // Delivery of an activity to a class is captured here via hasClass; no separate
            // "assigned" event — it would duplicate this exact moment.
            logEvent("VTG - Vocab Training Shared", {
                // trainingId is grandfathered — kept so the existing series stays continuous.
                // training_id is the canonical name `started share flow` uses.
                trainingId,
                training_id: trainingId,
                hasClass: effectiveSchoolClassId !== null,
                // Which class received the share. Omitted on a class-less share rather than sent
                // empty, so the property never carries a value that isn't a real class.
                ...(effectiveSchoolClassId ? { class_id: effectiveSchoolClassId } : {}),
                isScheduled,
            });

            await loadVocabTrainings();

            const invite = await resolveEmptyClassInviteModal({
                effectiveSchoolClassId,
                // Only set when this share created the class; otherwise the name comes from schoolClasses.
                createdClassName: createdSchoolClassId ? newClassForm?.name.trim() : undefined,
                schoolClasses,
            });

            if (invite) {
                setInviteModal(invite);
            } else {
                _showShareToast(t_sharedWithClass, ToastVariants.SUCCESS);
            }

            _resetSharePopupState();
        } finally {
            setShareInProgress(false);
        }
    };

    const handleOpenEditActivityVisibility = (training: AeaExerciseModel) => {
        if (!training.id) return;
        setEditActivityVisibilityPopup({ trainingId: training.id });
    };

    const handleCloseEditActivityVisibility = () => {
        setEditActivityVisibilityPopup(null);
    };

    const handleSaveVocabVisibility = async (params: { visibilityDate: Moment | null }) => {
        if (!editActivityVisibilityPopup) return;
        const trainingId = editActivityVisibilityPopup.trainingId;
        const training = vocabTrainings.find((vt) => vt.id === trainingId);
        if (!training) return;

        // Refetch on cache miss so we don't fabricate bookName/shareLink/etc. and clobber BE state.
        let existingMetadata: VocabTrainingMetadataPayload | undefined = vocabMetadata[trainingId];
        if (!existingMetadata) {
            try {
                existingMetadata = await getVocabTrainingMetadata(trainingId);
            } catch {
                _showShareToast(t_errEditVisibility, ToastVariants.ERROR);
                return;
            }
        }
        if (!existingMetadata) {
            _showShareToast(t_errEditVisibility, ToastVariants.ERROR);
            return;
        }

        const previousMetadata: VocabTrainingMetadataPayload = existingMetadata;

        const isPublishing = !!params.visibilityDate;
        let shareLink = previousMetadata.shareLink;
        if (isPublishing && !shareLink) {
            shareLink = await _resolveShareLinkForPublish(trainingId, training, previousMetadata);
            if (!shareLink) {
                _reportMissingShareLink(trainingId);
                _showShareToast(t_errEditVisibility, ToastVariants.ERROR);
                return;
            }
        }

        const optimistic: VocabTrainingMetadataPayload = {
            ...previousMetadata,
            testUuid: trainingId,
            shareLink,
            // Rows written before these fields existed carry neither, and re-posting without them
            // would drop the training out of recent-articles. Backfill instead of propagating undefined.
            teacherId: previousMetadata.teacherId || userId,
            articleUuid: previousMetadata.articleUuid ?? resolveArticleUuid(training, userId),
            json: await _resolveMetadataJson(training, previousMetadata.json),
            schoolClassId: training.groupId ?? previousMetadata.schoolClassId ?? null,
            visibilityDate: params.visibilityDate ? params.visibilityDate.valueOf() : null,
        };
        setVocabMetadata((prev) => ({ ...prev, [trainingId]: optimistic }));

        try {
            await shareVocabTrainingMetadata(optimistic);
            handleCloseEditActivityVisibility();
            _showShareToast(t_visibilityUpdated, ToastVariants.SUCCESS);
        } catch {
            // Last-write-wins: only roll back if no newer write replaced our optimistic value.
            setVocabMetadata((prev) =>
                prev[trainingId] === optimistic ? { ...prev, [trainingId]: previousMetadata } : prev
            );
            _showShareToast(t_errEditVisibility, ToastVariants.ERROR);
        }
    };

    return {
        vocabTrainings,
        vocabMetadata,
        vocabTrainingsDataLoaded,
        vocabTrainingsLoadError,
        loadVocabTrainings,
        resolveVocabTrainingInfos,
        vocabTrainingDataToDelete,
        setVocabTrainingDataToDelete,
        handleOpenDeleteVocabTrainingModal,
        handleConfirmDeleteVocabTraining,
        duplicatePopup,
        keepClassOnDuplicate,
        handleOpenDuplicateVocabTrainingPopup,
        handleCloseDuplicateVocabTrainingPopup,
        toggleKeepClassOnDuplicate,
        handleConfirmDuplicateVocabTraining,

        sharePopup,
        shareInProgress,
        shareToast,
        setShareToast,
        handleShareButtonClick,
        handleCloseSharePopup,
        handleSharePrimaryAction,
        isFinalShareAction,
        handleSharePrevStep,
        handleSelectShareClass,
        handleSelectShareTiming,
        handleShareVisibilityDateChange,
        handleOpenNewClassForm,
        handleCloseNewClassForm,
        handleCollapseNewClassForm,
        handleNewClassNameChange,
        handleToggleNewClassConsent,
        handleConfirmShare,

        shareLinkPopupTraining,
        handleCloseShareLinkPopup,
        handleVocabTrainingLinkCopied,

        inviteModal,
        handleCloseInviteModal,
        handleInviteNavigate,

        editActivityVisibilityPopup,
        handleOpenEditActivityVisibility,
        handleCloseEditActivityVisibility,
        handleSaveVocabVisibility,
    };
};
